import React from 'react';
import { Alert, Badge } from '@parsina/framework';
import { TriangleAlert, Sparkles } from 'lucide-react';

import { C, CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';

// The two sentences this app is not allowed to whisper.
//
// student-app/docs/CORE.md §6: "It gives teaching explanations, not clinical
// advice. Nobody is being treated. Every screen that renders an AI answer
// carries that, in Persian, where the student reads it — not in a settings page,
// not in a ToS. This is the one piece of copy in the app that is not
// negotiable." §3 says the same about the case itself: the patient is invented,
// and the boundary between this app and the real patient registry is the reason
// the whole architecture exists.
//
// They live in ONE module for the obvious reason and one less obvious one: the
// obvious one is that four screens render them and four copies drift; the less
// obvious one is that a disclaimer nobody can find is a disclaimer nobody can
// audit. Change the words here and every surface changes with them.
//
// WHERE THEY GO — and it is not the footer:
//
//   <PracticeBanner/>  above the case list and at the top of a case record, in
//                      the reading column, before the student types a name.
//   <PracticeBadge/>   on every row and header that names a case, so a case is
//                      never merely "a patient" anywhere on screen.
//   <AiDisclaimer/>    at the top of the assistant, above the tabs, always.
//   <AiAnswerNote/>    under EVERY rendered answer — the chat bubble, the
//                      differential card, the interaction result. An answer that
//                      is screenshotted, scrolled to, or read after the page has
//                      been open for an hour still carries it.

/**
 * «این بیمار خیالی است» — the case-is-fake banner.
 *
 * `warning`, not `info`: it is not a tip, it is the boundary. A student who
 * types a real کد ملی into `caseRef` has done the one thing this app exists to
 * prevent, and `practice.banner.body` names all three identifiers explicitly
 * rather than saying "personal information", which nobody has ever read as
 * "including the number on my own ID card".
 */
export function PracticeBanner({ style }) {
  const { t } = useTranslation();
  return (
    <Alert
      variant="warning"
      title={t('practice.banner.title')}
      icon={<TriangleAlert size={18} aria-hidden />}
      data-testid="practice-banner"
      style={style}
    >
      {t('practice.banner.body')}
    </Alert>
  );
}

/**
 * The «تمرینی» chip that rides beside a case's name.
 *
 * `short` is for a table cell or a mobile card, where the full word costs a
 * column. Both render the SAME meaning — this is not a place to abbreviate down
 * to an icon, because an icon is a thing a student learns to stop seeing.
 */
export function PracticeBadge({ short = false, style }) {
  const { t } = useTranslation();
  return (
    <Badge variant="warning" style={style} data-testid="practice-badge">
      {t(short ? 'practice.badge.short' : 'practice.badge')}
    </Badge>
  );
}

/**
 * «پاسخ آموزشی است، نه توصیهٔ بالینی» — the assistant's standing disclaimer.
 *
 * Rendered ABOVE the tabs rather than inside one, so switching from گفتگو to
 * تشخیص افتراقی never scrolls it off. It is the first thing on the assistant
 * screen and it does not collapse: a dismissible safety notice is one that is
 * dismissed on the first session and never seen again.
 */
export function AiDisclaimer({ style }) {
  const { t } = useTranslation();
  return (
    <Alert
      variant="info"
      title={t('practice.ai.title')}
      icon={<Sparkles size={18} aria-hidden />}
      data-testid="ai-disclaimer"
      style={style}
    >
      {t('practice.ai.body')}
    </Alert>
  );
}

/**
 * The one-line reminder that sits UNDER an answer.
 *
 * Small and quiet on purpose — it is not competing with the answer for
 * attention, it is travelling with it. The banner above states the case; this is
 * what survives a screenshot of a single differential card.
 */
export function AiAnswerNote({ style }) {
  const { t } = useTranslation();
  return (
    <p
      data-testid="ai-answer-note"
      style={{
        margin: '8px 0 0',
        fontSize: 11.5,
        lineHeight: 1.8,
        color: CT.mute,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        ...style,
      }}
    >
      <Sparkles size={12} color={C.teal} aria-hidden />
      {t('practice.ai.short')}
    </p>
  );
}

export default PracticeBanner;
